import styled, { css } from 'styled-components';
import type { ThemeProps } from 'styled-components';
import type { ChoiceType } from './types';
import type { ThemeType } from '../../util/types';
import type { InputHTMLAttributes } from 'react';
import {
  CHECKBOX_SIZE,
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNC,
  getFocusStyle,
  getPolishedColors,
} from '../../util/styled';
import { CHOICE_CHECKBOX, CHOICE_RADIO } from '../../util/data-id';
import { Z_INDEXES } from '../../util/vars';

const getFillStyles = (props: ThemeProps<ThemeType> & {
  $isChecked: boolean;
  $isDisabled: boolean
}) => {
  const colors = getPolishedColors({ theme: props.theme, $color: 'header' });

  if (props.$isDisabled) {
    return css`
      border-color: ${props.theme.colors.light};
      background-color: ${props.theme.colors.light};
      [data-id='${CHOICE_RADIO}'] svg {
        color: ${props.$isChecked ? props.theme.colors.dark : 'transparent'};
      }
      [data-id='${CHOICE_CHECKBOX}'] svg {
        color: ${props.$isChecked ? props.theme.colors.dark : 'transparent'};
      }       
    `;
  }

  if (props.$isChecked) {
    return css`      
      background-color: ${colors.main.color};
      [data-id='${CHOICE_RADIO}'] svg {
        color: ${colors.main.readable};      
      }
      [data-id='${CHOICE_CHECKBOX}'] svg {
        color: ${colors.main.readable};
      }
  `;
  }

  return css`
    border-color: ${colors.main.color};
    background-color: white;
    [data-id='${CHOICE_RADIO}'] svg {
      color: transparent;      
    }
    [data-id='${CHOICE_CHECKBOX}'] svg {
      color: transparent;
    }
  `;
};

export const ChoiceWrapStyled = styled('div')<{
  $type: ChoiceType;
  $isChecked: boolean;
  $isDisabled: boolean
}>`
  position: relative;
  display: flex;    
  height: ${CHECKBOX_SIZE}px;
  width: ${CHECKBOX_SIZE}px;
  border-radius: ${(props) => props.$type === 'checkbox' ? `calc(${props.theme.radius.default}/2)` : props.theme.radius.button};
  border: 2px solid transparent;  
  
  [data-id='${CHOICE_RADIO}'],
  [data-id='${CHOICE_CHECKBOX}'] {
    position: relative;
    z-index: ${Z_INDEXES.level_1};
    display: ${(props) => props.$isChecked ? 'flex' : 'none'};
    align-items: center;
    justify-items: center;    
    margin: auto;
  }  

  [data-id='${CHOICE_RADIO}'],    
  [data-id='${CHOICE_CHECKBOX}'] {
    &, svg {
      width: 100%;
      height: 100%;    
    }   
  } 
  
  ${(props) => getFillStyles(props)}
`;

export const ChoiceStyled = styled('input').attrs((props: InputHTMLAttributes<HTMLInputElement>) => ({
  name: props.name || props.id,
  required: props.required || false,
  checked: props.checked,
  disabled: props.disabled,
}))<{ type: ChoiceType }>`
  appearance: none!important;  
  padding: 0!important;
  margin: 0!important;  
  box-sizing: border-box;  
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;  
  z-index: ${Z_INDEXES.level_1 + 1};
  height: 100%;
  width: 100%;
  color: transparent !important;
  border-color: transparent !important;
  border-radius: ${(props) => props.type === 'checkbox' ? `calc(${props.theme.radius.default}/2)` : props.theme.radius.button};
  
  transition: box-shadow ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
  
  ${(props) => !props.disabled && 'cursor: pointer;'}
  
  &,
  &:hover,
  &:checked,
  &:disabled,
  &[readonly] {
    background-image: none !important;
    background-color: transparent !important;
  }
  
  &:focus {
    ${(props) => getFocusStyle(props.theme.colors.primary)}
  }
`;
